'use client';

import { ChangeEvent, FocusEvent, FormEvent, useEffect, useRef, useState } from 'react';
import { useTimedNotification } from '@/hooks/useTimedNotification';
import type { InAppPurchasePopoverVariant } from '@/src/ui-components/organisms/Snapshot/InAppPurchasePopover/InAppPurchasePopover';
import type { PartnerSubscriptionVariant } from '@/src/ui-components/organisms/Snapshot/PartnerSubscription/PartnerSubscription';
import type { SnapshotAlertBannerProps } from './SnapshotAlertBanner';
import type { SignInMethod, SignInMethodOption, SnapshotUserErrorType, SnapshotUserStep } from './SnapshotUser.types';
import { getClientSdk } from './clientSdk';
import {
    buildMethodOptions,
    completeAuthentication,
    getAccountCountry,
    getMethodNotificationFromError,
    getVerificationNotificationFromError,
    mapIdentifierLookupError,
    mapOtpSendError,
    mapPasswordError,
    mapVerificationCodeError,
    PLANS_PATH,
    runEnvInfoPreflight,
    sendVerificationCode,
    type ClientSdk,
    type ContactOptionResponse,
    type PostLoginOutcome,
    type SignupStatusResponse,
    type SnapshotAuthenticatedUser,
    type TeslaManageSelectionData,
} from './snapshotSignInFlowServices';

interface UseSnapshotSignInFlowOptions {
    careBaseUrl: string;
    initialAccountCountry?: string;
    initialErrorType?: SnapshotUserErrorType;
    initialIdentifier?: string;
    initialNotificationBanner?: SnapshotAlertBannerProps;
    initialStep?: SnapshotUserStep;
    initialTeslaManageSelection?: TeslaManageSelectionData;
    onAuthenticated?: (data: SnapshotAuthenticatedUser) => void;
    onClose: () => void;
}

export const useSnapshotSignInFlow = ({
    careBaseUrl,
    initialAccountCountry,
    initialErrorType = 'none',
    initialIdentifier = '',
    initialNotificationBanner,
    initialStep = 'enterIdentifier',
    initialTeslaManageSelection,
    onAuthenticated,
    onClose,
}: UseSnapshotSignInFlowOptions) => {
    const envInfoPreflightRef = useRef<Promise<void> | null>(null);
    const [identifier, setIdentifier] = useState(initialIdentifier);
    const [errorType, setErrorType] = useState<SnapshotUserErrorType>(initialErrorType);
    const [hasSubmittedIdentifierStep, setHasSubmittedIdentifierStep] = useState(false);
    const [hasSubmittedVerificationCodeStep, setHasSubmittedVerificationCodeStep] = useState(false);
    const [step, setStep] = useState<SnapshotUserStep>(initialStep);
    const [selectedMethod, setSelectedMethod] = useState<SignInMethod>('password');
    const [password, setPassword] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const {
        clear: clearVerificationNotification,
        notification: timedVerificationNotification,
        show: showVerificationNotification,
    } = useTimedNotification<string>({ durationMs: 3000, exitDelayMs: 400 });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [methodOptions, setMethodOptions] = useState<SignInMethodOption[]>([]);
    const [identityId, setIdentityId] = useState('');
    const [contactOptions, setContactOptions] = useState<ContactOptionResponse[]>([]);
    const [accountCountry, setAccountCountry] = useState(initialAccountCountry);
    const [identityState, setIdentityState] = useState<string | undefined>();
    const [partnerSubscriptionVariant, setPartnerSubscriptionVariant] = useState<
        PartnerSubscriptionVariant | undefined
    >();
    const [teslaManageSelection, setTeslaManageSelection] = useState<TeslaManageSelectionData | undefined>(
        initialTeslaManageSelection,
    );
    const [inAppPurchaseVariant, setInAppPurchaseVariant] = useState<InAppPurchasePopoverVariant | undefined>();
    const [inAppPurchaseSubscribeHref, setInAppPurchaseSubscribeHref] = useState<string | undefined>();
    const [methodNotificationBanner, setMethodNotificationBanner] = useState<SnapshotAlertBannerProps | undefined>(
        initialNotificationBanner,
    );
    const verificationNotification = timedVerificationNotification
        ? {
              id: timedVerificationNotification.id,
              message: timedVerificationNotification.value,
              phase: timedVerificationNotification.phase,
          }
        : undefined;

    const pushUserError = (errorName: string) => {
        (window as BrowserWindow).pushToAdobe({
            event: 'user-error',
            errorInfo: [{ errorType: 'USER', errorName, errorCode: '' }],
        });
    };

    const pushSystemError = (errorName: string, errorCode = '') => {
        (window as BrowserWindow).pushToAdobe({
            event: 'system-error',
            errorInfo: [{ errorType: 'SYSTEM', errorName, errorCode }],
        });
    };

    const pushBusinessError = (errorName: string, errorCode = '') => {
        (window as BrowserWindow).pushToAdobe({
            event: 'business-error',
            errorInfo: [{ errorType: 'BUSINESS', errorName, errorCode }],
        });
    };

    const ensureEnvInfoPreflight = () => {
        if (!envInfoPreflightRef.current) {
            envInfoPreflightRef.current = runEnvInfoPreflight().catch(() => undefined);
        }

        return envInfoPreflightRef.current;
    };

    useEffect(() => {
        void ensureEnvInfoPreflight();
    }, []);
    const resetCredentialStepState = () => {
        setPassword('');
        setVerificationCode('');
        clearVerificationNotification();
        setMethodNotificationBanner(undefined);
    };

    const signOutAfterSpecialCaseAuthentication = async (clientSdk: ClientSdk) => {
        try {
            await clientSdk.user.signOut();
        } catch {}
    };

    const applyPostLoginOutcome = async (clientSdk: ClientSdk, outcome: PostLoginOutcome) => {
        if (outcome.kind === 'automotiveTrial') {
            await signOutAfterSpecialCaseAuthentication(clientSdk);
            resetCredentialStepState();
            setPartnerSubscriptionVariant('tesla');
            setTeslaManageSelection(undefined);
            setInAppPurchaseVariant(undefined);
            setInAppPurchaseSubscribeHref(undefined);
            setStep('partnerSubscription');
            return;
        }

        if (outcome.kind === 'countryMismatch') {
            await signOutAfterSpecialCaseAuthentication(clientSdk);
            setAccountCountry(outcome.accountCountry);
            resetCredentialStepState();
            setSelectedMethod('password');
            setTeslaManageSelection(undefined);
            setStep('enterPassword');
            return;
        }

        if (outcome.kind === 'streamingIdentity') {
            await signOutAfterSpecialCaseAuthentication(clientSdk);
            setIdentityState('STREAMING');
            resetCredentialStepState();
            setSelectedMethod('password');
            setTeslaManageSelection(undefined);
            setStep('enterPassword');
            return;
        }

        if (outcome.kind === 'nonStandardIdentity') {
            await signOutAfterSpecialCaseAuthentication(clientSdk);
            setIdentityState(outcome.identityState);
            resetCredentialStepState();
            setSelectedMethod('password');
            setTeslaManageSelection(undefined);
            setStep('enterPassword');
            return;
        }

        if (outcome.kind === 'incompleteTransaction') {
            await signOutAfterSpecialCaseAuthentication(clientSdk);
            resetCredentialStepState();
            setPartnerSubscriptionVariant(undefined);
            setTeslaManageSelection(undefined);
            setInAppPurchaseVariant('incomplete-transaction');
            setInAppPurchaseSubscribeHref(PLANS_PATH);
            setStep('partnerSubscription');
            return;
        }

        if (outcome.kind === 'teslaManageSelection') {
            resetCredentialStepState();
            setPartnerSubscriptionVariant(undefined);
            setTeslaManageSelection(outcome.selection);
            setInAppPurchaseVariant(undefined);
            setInAppPurchaseSubscribeHref(undefined);
            setStep('partnerSubscription');
            return;
        }

        if (outcome.kind === 'partnerManaged') {
            await signOutAfterSpecialCaseAuthentication(clientSdk);
            resetCredentialStepState();
            setPartnerSubscriptionVariant(outcome.modalVariant.partnerSubscriptionVariant);
            setTeslaManageSelection(undefined);
            setInAppPurchaseVariant(outcome.modalVariant.inAppPurchaseVariant);
            setInAppPurchaseSubscribeHref(outcome.modalVariant.subscribeHref);
            setStep('partnerSubscription');
            return;
        }

        if (outcome.kind === 'smsLegacyLoginRequired') {
            throw new Error('SMS legacy login outcome must be handled before applying the final auth state');
        }

        onAuthenticated?.({
            ...outcome.authenticatedUser,
            userName: outcome.authenticatedUser.userName || identifier.trim(),
        });
        onClose();
    };
    const requestVerificationCode = async () => {
        const clientSdk = await getClientSdk();

        if (!clientSdk || !identityId) {
            setErrorType('sdkUnavailable');
            throw new Error('SDK unavailable');
        }

        setIsSubmitting(true);
        setErrorType('none');

        try {
            await ensureEnvInfoPreflight();
            await sendVerificationCode({
                clientSdk,
                nextIdentityId: identityId,
                method: selectedMethod,
            });
            setErrorType('none');
        } catch (error) {
            clearVerificationNotification();
            setErrorType(mapOtpSendError(error));
            throw error;
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRequestNewCode = async () => {
        try {
            await requestVerificationCode();
            showVerificationNotification('New code sent');
            setMethodNotificationBanner(undefined);
        } catch (error) {
            const nextErrorType = mapOtpSendError(error);

            setMethodNotificationBanner(getVerificationNotificationFromError(nextErrorType));
        }
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setHasSubmittedIdentifierStep(true);

        if (!identifier.trim()) {
            setErrorType('blank');
            pushUserError('identifier blank');
            return;
        }

        const clientSdk = await getClientSdk();

        if (!clientSdk) {
            setErrorType('sdkUnavailable');
            pushSystemError('sdk unavailable');
            return;
        }

        setIsSubmitting(true);
        setErrorType('none');

        try {
            await ensureEnvInfoPreflight();

            const statusResponse = (await clientSdk.user.getStatusForSignup({
                handle: identifier.trim(),
            })) as SignupStatusResponse;
            const nextMethodOptions = buildMethodOptions(statusResponse);
            const nextSelectedMethod = nextMethodOptions[0]?.value ?? 'email';

            setPassword('');
            setVerificationCode('');
            setIdentityId(statusResponse.identityId);
            setContactOptions(statusResponse.contactOptions ?? []);
            setAccountCountry(getAccountCountry(statusResponse));
            setMethodOptions(nextMethodOptions);
            setSelectedMethod(nextSelectedMethod);
            setMethodNotificationBanner(undefined);
            clearVerificationNotification();
            setErrorType('none');
            setHasSubmittedIdentifierStep(false);
            setHasSubmittedVerificationCodeStep(false);

            if (nextMethodOptions.length <= 1) {
                if (nextSelectedMethod === 'password') {
                    setStep('enterPassword');
                    return;
                }

                try {
                    await sendVerificationCode({
                        clientSdk,
                        nextIdentityId: statusResponse.identityId,
                        method: nextSelectedMethod,
                    });
                    setHasSubmittedVerificationCodeStep(false);
                    setStep('enterVerificationCode');
                    return;
                } catch (error) {
                    clearVerificationNotification();
                    const nextErrorType = mapOtpSendError(error);

                    setErrorType(nextErrorType);
                    setMethodNotificationBanner(getMethodNotificationFromError(nextErrorType));
                    if (nextErrorType === 'otpSendFailed') {
                        pushSystemError('otp send failed');
                    } else if (nextErrorType === 'otpSendRateLimited') {
                        pushBusinessError('otp send rate limited');
                    }
                    setStep('chooseSignInMethod');
                    return;
                }
            }

            setStep('chooseSignInMethod');
        } catch (error) {
            const nextErrorType = mapIdentifierLookupError(error);
            setErrorType(nextErrorType);
            if (nextErrorType === 'notFound') {
                pushBusinessError('identifier notFound');
            } else if (nextErrorType === 'sdkUnavailable') {
                pushSystemError('sdk unavailable');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleMethodContinue = async () => {
        setErrorType('none');

        if (selectedMethod === 'password') {
            clearVerificationNotification();
            setMethodNotificationBanner(undefined);
            setStep('enterPassword');
            return;
        }

        try {
            await requestVerificationCode();
            setVerificationCode('');
            setHasSubmittedVerificationCodeStep(false);
            setMethodNotificationBanner(undefined);
            setErrorType('none');
            setStep('enterVerificationCode');
        } catch (error) {
            const nextErrorType = mapOtpSendError(error);

            setErrorType(nextErrorType);
            setMethodNotificationBanner(getMethodNotificationFromError(nextErrorType));
        }
    };

    const handlePasswordSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!password.trim()) {
            setErrorType('blankPassword');
            pushUserError('password blank');
            return;
        }

        const clientSdk = await getClientSdk();

        if (!clientSdk) {
            setErrorType('sdkUnavailable');
            pushSystemError('sdk unavailable');
            return;
        }

        setIsSubmitting(true);
        setErrorType('none');

        try {
            await ensureEnvInfoPreflight();

            await clientSdk.user.login({
                handle: identifier.trim(),
                password,
            });
            setPassword('');
        } catch (error) {
            const nextErrorType = mapPasswordError(error);
            setErrorType(nextErrorType);
            setIsSubmitting(false);
            if (nextErrorType === 'invalidCredentials') {
                pushBusinessError('password invalidCredentials');
            } else if (nextErrorType === 'sdkUnavailable') {
                pushSystemError('sdk unavailable');
            }
            return;
        }

        try {
            const outcome = await completeAuthentication(clientSdk, careBaseUrl);
            await applyPostLoginOutcome(clientSdk, outcome);
        } catch {
            setErrorType('sdkUnavailable');
            pushSystemError('sdk unavailable');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleVerificationCodeSubmit = async (_event: FormEvent, data: FormData) => {
        const submittedVerificationCode = String(data.get('verification-code') ?? '').trim();
        setHasSubmittedVerificationCodeStep(true);

        if (!submittedVerificationCode) {
            setErrorType('blankVerificationCode');
            pushUserError('verificationCode blank');
            return;
        }

        const clientSdk = await getClientSdk();

        if (!clientSdk || !identityId) {
            setErrorType('sdkUnavailable');
            pushSystemError('sdk unavailable');
            return;
        }

        setIsSubmitting(true);
        setErrorType('none');

        try {
            await ensureEnvInfoPreflight();
            await clientSdk.user.authenticateOTP(identityId, submittedVerificationCode);
            setVerificationCode('');
            setMethodNotificationBanner(undefined);
        } catch (error) {
            const nextErrorType = mapVerificationCodeError(error);

            setErrorType(nextErrorType);
            setMethodNotificationBanner(getVerificationNotificationFromError(nextErrorType));
            if (nextErrorType === 'verificationCodeInvalid' || nextErrorType === 'verificationCodeExpired') {
                pushBusinessError(
                    `verificationCode ${nextErrorType === 'verificationCodeInvalid' ? 'invalid' : 'expired'}`,
                );
            } else if (nextErrorType === 'verificationCodeRateLimited') {
                pushBusinessError('verificationCode rate limited');
            }
            setIsSubmitting(false);
            return;
        }

        try {
            const outcome = await completeAuthentication(clientSdk, careBaseUrl);
            await applyPostLoginOutcome(clientSdk, outcome);
        } catch {
            setErrorType('sdkUnavailable');
            pushSystemError('sdk unavailable');
            setMethodNotificationBanner(undefined);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleIdentifierChange = (event: ChangeEvent<HTMLInputElement>) => {
        setIdentifier(event.target.value);
        setAccountCountry(undefined);
        setIdentityState(undefined);
        setHasSubmittedIdentifierStep(false);

        if (errorType !== 'none') {
            setErrorType('none');
        }
    };

    const handleIdentifierBlur = (event: FocusEvent<HTMLInputElement>) => {
        if (!event.currentTarget.value.trim()) {
            setErrorType('blank');
        }
    };

    const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
        setPassword(event.target.value);

        if (errorType !== 'none') {
            setErrorType('none');
        }
    };

    const handleVerificationCodeChange = (nextValue: string) => {
        setVerificationCode(nextValue);
        setHasSubmittedVerificationCodeStep(false);
        setMethodNotificationBanner(undefined);

        if (errorType !== 'none') {
            setErrorType('none');
        }
    };

    const handleVerificationCodeBlur = (event: FocusEvent<HTMLInputElement>) => {
        if (!event.currentTarget.value.trim()) {
            setMethodNotificationBanner(undefined);
            setErrorType('blankVerificationCode');
        }
    };

    const getSelectedMethodDescription = () =>
        selectedMethod === 'text'
            ? contactOptions.find((option) => option.type === 'PHONE')?.maskedValue
            : contactOptions.find((option) => option.type === 'EMAIL')?.maskedValue;

    const handleMethodBack = () => {
        setErrorType('none');
        setMethodNotificationBanner(undefined);
        setStep('enterIdentifier');
    };

    const handleSelectMethod = (method: SignInMethod) => {
        setSelectedMethod(method);
        setMethodNotificationBanner(undefined);
    };

    const handleCredentialBack = () => {
        setErrorType('none');
        setMethodNotificationBanner(undefined);
        setPassword('');
        setVerificationCode('');
        setStep('chooseSignInMethod');
    };

    const handlePartnerBack = () => {
        setErrorType('none');
        setMethodNotificationBanner(undefined);
        setTeslaManageSelection(undefined);
        setStep('chooseSignInMethod');
    };

    const handlePartnerDismiss = () => {
        setErrorType('none');
        setMethodNotificationBanner(undefined);
        setPassword('');
        setTeslaManageSelection(undefined);
        setVerificationCode('');
        setStep('enterIdentifier');
    };

    return {
        accountCountry,
        errorType,
        handleCredentialBack,
        handleIdentifierBlur,
        handleIdentifierChange,
        handleMethodBack,
        handleMethodContinue,
        handlePartnerBack,
        handlePartnerDismiss,
        handlePasswordChange,
        handlePasswordSubmit,
        handleRequestNewCode,
        handleSelectMethod,
        handleSubmit,
        handleVerificationCodeBlur,
        handleVerificationCodeChange,
        handleVerificationCodeSubmit,
        hasSubmittedIdentifierStep,
        hasSubmittedVerificationCodeStep,
        identifier,
        identityState,
        inAppPurchaseSubscribeHref,
        inAppPurchaseVariant,
        isSubmitting,
        methodNotificationBanner,
        methodOptions,
        partnerSubscriptionVariant,
        password,
        selectedMethod,
        selectedMethodDescription: getSelectedMethodDescription(),
        step,
        teslaManageSelection,
        verificationCode,
        verificationNotification,
    };
};
